import { type ReactElement, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { CanvasPosition, Importance, TileSize } from '../../../types/infrastructure-map'
import { DotTile, FullTile } from './tile-node/tile-node.parts'
import {
  DEVICE_TYPE_ICONS,
  IMPORTANCE_BORDER_CLASSES,
  TILE_SIZE_CONFIG,
} from './tile-node/tile-node.helpers'
import { useTileDrag } from './tile-node/use-tile-drag'

interface TileNodeProps {
  endpointId: string
  hostname: string
  ipAddress: string
  deviceType: string
  role?: string | undefined
  tileSize: TileSize
  importance: Importance
  isSelected: boolean
  isLinkSource: boolean
  isInCollectionSet: boolean
  position: CanvasPosition
  scale: number
  onDrag: (position: CanvasPosition) => void
  onDragEnd: (position: CanvasPosition) => void
  onClick: (event: React.MouseEvent) => void
  onContextMenu: (event: React.MouseEvent) => void
}

export function TileNode(props: TileNodeProps): ReactElement {
  const { t } = useTranslation('cases')
  const nodeRef = useRef<HTMLDivElement>(null)
  const { isDragging, handleMouseDown } = useTileDrag({
    nodeRef,
    scale: props.scale,
    position: props.position,
    onDrag: props.onDrag,
    onDragEnd: props.onDragEnd,
  })

  const config = TILE_SIZE_CONFIG[props.tileSize]
  const icon = DEVICE_TYPE_ICONS[props.deviceType] ?? DEVICE_TYPE_ICONS.unknown ?? '❓'
  const borderClass = IMPORTANCE_BORDER_CLASSES[props.importance]

  const handleClick = (event: React.MouseEvent): void => {
    event.stopPropagation()
    props.onClick(event)
  }

  const handleContextMenuEvent = (event: React.MouseEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    props.onContextMenu(event)
  }

  if (props.tileSize === 'dot') {
    return (
      <DotTile
        ref={nodeRef}
        endpointId={props.endpointId}
        hostname={props.hostname}
        ipAddress={props.ipAddress}
        position={props.position}
        borderClass={borderClass}
        isSelected={props.isSelected}
        isLinkSource={props.isLinkSource}
        isDragging={isDragging}
        onMouseDown={handleMouseDown}
        onContextMenu={handleContextMenuEvent}
        onClick={handleClick}
      />
    )
  }

  return (
    <FullTile
      ref={nodeRef}
      endpointId={props.endpointId}
      hostname={props.hostname}
      ipAddress={props.ipAddress}
      deviceType={props.deviceType}
      role={props.role}
      tileSize={props.tileSize}
      borderClass={borderClass}
      icon={icon}
      position={props.position}
      config={config}
      isSelected={props.isSelected}
      isLinkSource={props.isLinkSource}
      isDragging={isDragging}
      isInCollectionSet={props.isInCollectionSet}
      collectionTitle={t('infrastructure.canvas.inCollectionSet')}
      onMouseDown={handleMouseDown}
      onContextMenu={handleContextMenuEvent}
      onClick={handleClick}
    />
  )
}

export default TileNode
